<template>
  <div
    class="collage"
    :style="{ '--collage-gap': gap + 'px' }"
    role="region"
  >
    <template v-if="mobile">
      <!-- Mobile: flatten all blocks as before -->
      <ul class="collage__grid">
        <template v-for="(block, blockIdx) of visibleBlocks">
          <li
            v-for="imgIdx of getBlockImageIndexes(block)"
            :key="`${blockIdx}-${imgIdx}`"
            class="collage__item"
            :class="{ 'collage__item--no-outline': blockNoOutlineKey === `${blockIdx}-${imgIdx}` }"
            :aria-label="block[`title_${imgIdx}`] || 'Collage block'"
            @focusin="handleItemFocusIn($event, `${blockIdx}-${imgIdx}`)"
            @focusout="handleItemFocusOut($event, `${blockIdx}-${imgIdx}`)"
          >
            <div class="collage__media">
              <template v-if="block[`image_url_${imgIdx}`]">
                <a
                  :href="block[`image_url_${imgIdx}`]"
                  class="collage__image-link"
                  :aria-label="block[`title_${imgIdx}`] || 'Collage image'"
                >
                  <video-player
                    v-if="hasVideo(block, imgIdx)"
                    class="collage__video collage-block__video-player-controls"
                    :video="getResponsiveVideo(block, imgIdx)"
                    :image="getPreviewImage(block, imgIdx)"
                    lazyload
                    is-autoplay="true"
                    loop="true"
                    muted="true"
                    :ratio="130/156"
                    :ratio-desktop="288/344"
                    :use-overlay-button="false"
                    :icon-color="getVideoIconColor(block, imgIdx)"
                  />
                  <art-direction
                    v-else
                    :image="getResponsiveImage(block, imgIdx)"
                    :image-desktop="getImageObject(block[`image_${imgIdx}`])"
                    :alt="block[`title_${imgIdx}`] || 'Collage image'"
                    class="collage__image"
                    lazyload
                    :ratio="130/156"
                    :ratio-desktop="288/344"
                  />
                </a>
              </template>
              <template v-else>
                <video-player
                  v-if="hasVideo(block, imgIdx)"
                  class="collage__video collage-block__video-player-controls"
                  :video="getResponsiveVideo(block, imgIdx)"
                  :image="getPreviewImage(block, imgIdx)"
                  lazyload
                  is-autoplay="true"
                  loop="true"
                  muted="true"
                  :ratio="130/156"
                  :ratio-desktop="288/344"
                  :use-overlay-button="false"
                  :icon-color="getVideoIconColor(block, imgIdx)"
                />
                <art-direction
                  v-else
                  :image="getResponsiveImage(block, imgIdx)"
                  :image-desktop="getImageObject(block[`image_${imgIdx}`])"
                  :alt="block[`title_${imgIdx}`] || 'Collage image'"
                  class="collage__image"
                  lazyload
                  :ratio="130/156"
                  :ratio-desktop="288/344"
                />
              </template>
              <!-- Gradient Overlay -->
              <div
                v-if="block[`gradient_enable_${imgIdx}`]"
                class="collage-block__gradient-overlay"
                :style="getGradientOverlayStyle(block, imgIdx)"
              />
              <!-- Overlay Blocks -->
              <template
                v-for="type of ['title', 'subtitle', 'description']"
                :key="type"
              >
                <div
                  :class="[
                    `collage-block__${type}-overlay`,
                    getOverlayClass(type, block, imgIdx, overlayFallback[type])
                  ]"
                  :style="getOverlayStyle(block, imgIdx, type)"
                >
                  <component
                    :is="overlayTag[type]"
                    :class="`collage-block__${type} ${type === 'title' ? 'text-heading-m' : ''}`"
                  >
                    {{ block[`${type}_${imgIdx}`] }}
                  </component>
                </div>
              </template>
            </div>
          </li>
        </template>
      </ul>
    </template>
    <template v-else>
      <!-- Desktop: render each row separately -->
      <div class="collage__rows">
        <ul
          v-for="(block, blockIdx) of visibleBlocks"
          :key="blockIdx"
          class="collage__grid collage__grid--row"
        >
          <li
            v-for="imgIdx of getBlockImageIndexes(block)"
            :key="`${blockIdx}-${imgIdx}`"
            class="collage__item"
            :class="{ 'collage__item--no-outline': blockNoOutlineKey === `${blockIdx}-${imgIdx}` }"
            :tabindex="block[`image_url_${imgIdx}`] ? null : 0"
            :aria-label="block[`title_${imgIdx}`] || 'Collage block'"
            :style="getItemStyle(block)"
            @focusin="handleItemFocusIn($event, `${blockIdx}-${imgIdx}`)"
            @focusout="handleItemFocusOut($event, `${blockIdx}-${imgIdx}`)"
          >
            <div class="collage__media">
              <template v-if="block[`image_url_${imgIdx}`]">
                <a
                  :href="block[`image_url_${imgIdx}`]"
                  class="collage__image-link"
                  :aria-label="block[`title_${imgIdx}`] || 'Collage image'"
                >
                  <video-player
                    v-if="hasVideo(block, imgIdx)"
                    class="collage__video collage-block__video-player-controls"
                    :video="getResponsiveVideo(block, imgIdx)"
                    :image="getPreviewImage(block, imgIdx)"
                    lazyload
                    is-autoplay="true"
                    loop="true"
                    muted="true"
                    :ratio="130/156"
                    :ratio-desktop="288/344"
                    :use-overlay-button="false"
                    :icon-color="getVideoIconColor(block, imgIdx)"
                  />
                  <art-direction
                    v-else
                    :image="getResponsiveImage(block, imgIdx)"
                    :image-desktop="getImageObject(block[`image_${imgIdx}`])"
                    :alt="block[`title_${imgIdx}`] || 'Collage image'"
                    class="collage__image"
                    lazyload
                    :ratio="130/156"
                    :ratio-desktop="288/344"
                  />
                </a>
              </template>
              <template v-else>
                <video-player
                  v-if="hasVideo(block, imgIdx)"
                  class="collage__video collage-block__video-player-controls"
                  :video="getResponsiveVideo(block, imgIdx)"
                  :image="getPreviewImage(block, imgIdx)"
                  lazyload
                  is-autoplay="true"
                  loop="true"
                  muted="true"
                  :ratio="130/156"
                  :ratio-desktop="288/344"
                  :use-overlay-button="false"
                  :icon-color="getVideoIconColor(block, imgIdx)"
                />
                <art-direction
                  v-else
                  :image="getResponsiveImage(block, imgIdx)"
                  :image-desktop="getImageObject(block[`image_${imgIdx}`])"
                  :alt="block[`title_${imgIdx}`] || 'Collage image'"
                  class="collage__image"
                  lazyload
                  :ratio="130/156"
                  :ratio-desktop="288/344"
                />
              </template>
              <!-- Gradient Overlay -->
              <div
                v-if="block[`gradient_enable_${imgIdx}`]"
                class="collage-block__gradient-overlay"
                :style="getGradientOverlayStyle(block, imgIdx)"
              />
              <!-- Overlay Blocks -->
              <template
                v-for="type of ['title', 'subtitle', 'description']"
                :key="type"
              >
                <div
                  :class="[
                    `collage-block__${type}-overlay`,
                    getOverlayClass(type, block, imgIdx, overlayFallback[type])
                  ]"
                  :style="getOverlayStyle(block, imgIdx, type)"
                >
                  <component
                    :is="overlayTag[type]"
                    :class="`collage-block__${type} ${type === 'title' ? 'text-heading-m' : ''}`"
                  >
                    {{ block[`${type}_${imgIdx}`] }}
                  </component>
                </div>
              </template>
            </div>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>

/**
 * CollageBlock component
 * Renders a responsive collage of images and videos with overlays.
 *
 * Props:
 * - blocks: Array of block objects, each representing a row in the collage.
 * - gap: Gap between collage items.
 * - rows: Number of rows to display.
 */
import VideoPlayer from '~async/video-player/video-player'
import ArtDirection from '~global/images/art-direction'
import debounce from 'lodash/debounce'

export default {
  name: 'CollageBlock',
  components: {
    ArtDirection,
    VideoPlayer,
  },
  props: {
    blocks: {
      type: Array,
      required: true,
    },
    gap: {
      type: [String, Number],
      default: 1,
    },
    rows: {
      type: [String, Number],
      default: 2,
    },
  },
  data() {
    return {
      mobile: typeof window === 'undefined' ? false : window.innerWidth <= 768,
      blockImageIndexesMemo: new WeakMap(),
      blockNoOutlineKey: null,
      overlayFallback: {
        title: 'top-left',
        subtitle: 'top-right',
        description: 'bottom-left',
      },
      overlayTag: {
        title: 'h2',
        subtitle: 'p',
        description: 'p',
      },
    }
  },
  computed: {

    /**
     * Compute the visible blocks based on the number of rows.
     * @returns {Array}
     */
    visibleBlocks() {
      const rowCount = Number(this.rows) || 1
      return this.blocks.slice(0, rowCount)
    },
  },
  watch: {
    blocks: {
      handler() {
        this.blockImageIndexesMemo = new WeakMap()
      },
      deep: true,
    },
  },
  mounted() {
    this.debouncedResize = debounce(this.handleResize, 100)
    window.addEventListener('resize', this.debouncedResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.debouncedResize)
  },
  methods: {

    /**
     * Handles focusin event on a collage item.
     * If the focused element is a video control,
     * sets blockNoOutlineKey to the item's key.
     * @param {FocusEvent} event - The focusin event.
     * @param {string} key - The unique key for the collage item.
     */
    handleItemFocusIn(event, key) {
      const isVideoControl = event.target.closest('.collage-block__video-player-controls button, .collage-block__video-player-controls [tabindex]')
      if (isVideoControl) {
        this.blockNoOutlineKey = key
      }
    },

    /**
     * Handles focusout event on a collage item.
     * Resets blockNoOutlineKey to null.
     */
    handleItemFocusOut() {
      this.blockNoOutlineKey = null
    },

    /**
     * Handle window resize event to update mobile state.
     */
    handleResize() {
      this.mobile = window.innerWidth <= 768
    },

    /**
     * Get image object from string or return as is if already an object.
     * @param {String|Object} image - Image source or object.
     * @returns {Object}
     */
    getImageObject(image) {
      if (typeof image === 'string') {
        return { src: image }
      }
      return image
    },

    /**
     * Convert hex color to rgb string.
     * @param {String} hex - Hex color string.
     * @returns {String}
     */
    hexToRgb(hex) {
      let hexValue = hex.replace('#', '')
      if (hexValue.length === 3) {
        hexValue = hexValue.split('').map(char => char + char).join('')
      }
      const num = parseInt(hexValue, 16)
      const red = Math.floor(num / (256 * 256)) % 256
      const green = Math.floor(num / 256) % 256
      const blue = num % 256
      return `${red}, ${green}, ${blue}`
    },

    /**
     * Get the indexes of images to display for a block,
     * limited by collage_blocks_in_row. Memoized per block.
     * @param {Object} block - Block object.
     * @returns {Array}
     */
    getBlockImageIndexes(block) {
      if (this.blockImageIndexesMemo.has(block)) {
        return this.blockImageIndexesMemo.get(block)
      }
      const maxImages = Number(block.collage_blocks_in_row) || 5
      const indexes = Array.from({ length: maxImages }, (_, imgIndex) => imgIndex + 1)
        .filter(imgIdx => Boolean(block[`image_${imgIdx}`]))
      this.blockImageIndexesMemo.set(block, indexes)
      return indexes
    },

    /**
     * Get the gradient overlay style for a block and image index.
     * @param {Object} block
     * @param {Number} imgIdx
     * @returns {Object}
     */
    getGradientOverlayStyle(block, imgIdx) {
      const color = block[`gradient_color_${imgIdx}`] || '#000'
      const opacity = (block[`gradient_opacity_${imgIdx}`] || 50) / 100
      const rgb = this.hexToRgb(color)

      const direction = block[`gradient_direction_${imgIdx}`] || 'to-bottom'

      let gradientDirection
      switch (direction) {
        case 'to-top':
          gradientDirection = '0deg'
          break
        case 'to-right':
          gradientDirection = '90deg'
          break
        case 'to-left':
          gradientDirection = '270deg'
          break
        case 'to-bottom':
        default:
          gradientDirection = '180deg'
      }

      return {
        background: `linear-gradient(${gradientDirection}, rgba(${rgb}, ${opacity}) 0%, rgba(${rgb}, 0) 100%)`,
      }
    },

    /**
     * Get the responsive image object for a block and image index.
     * Returns mobile image on mobile, desktop image otherwise.
     * @param {Object} block
     * @param {Number} imgIdx
     * @returns {Object}
     */
    getResponsiveImage(block, imgIdx) {
      if (typeof window !== 'undefined' && window.innerWidth <= 768) {
        return this.getImageObject(block[`mobile_image_${imgIdx}`]) || this.getImageObject(block[`image_${imgIdx}`])
      }
      return this.getImageObject(block[`image_${imgIdx}`]) || this.getImageObject(block[`mobile_image_${imgIdx}`])
    },

    /**
     * Get the resolved color for title, subtitle, or description.
     * @param {Object} block - Block object.
     * @param {Number} imgIdx - Image index.
     * @param {String} type - 'title', 'subtitle', or 'description'
     * @returns {String}
     */
    getTextColor(block, imgIdx, type) {
      const colorType = block[`${type}_color_type_${imgIdx}`]
      if (colorType === 'black') {
        return 'var(--color-brand-1-black)'
      }
      if (colorType === 'white') {
        return 'var(--color-brand-1-white)'
      }
      return block[`${type}_color_${imgIdx}`] || 'var(--color-brand-1-black)'
    },

    /**
     * Check if the block has a valid video for the given image index.
     * @param {Object} block - Block object.
     * @param {Number} imgIdx - Image index.
     * @returns {Boolean}
     */
    hasVideo(block, imgIdx) {
      const videoObj = this.getResponsiveVideo(block, imgIdx)
      return Boolean(
        videoObj &&
          videoObj.sources &&
          Array.isArray(videoObj.sources) &&
          videoObj.sources.some(src => src.format === 'mp4' && src.url),
      )
    },

    /**
     * Get the responsive video object for a block and image index.
     * @param {Object} block - Block object.
     * @param {Number} imgIdx - Image index.
     * @returns {Object|null}
     */
    getResponsiveVideo(block, imgIdx) {
      const videoUrl = block[`block_video_url_${imgIdx}`]
      if (videoUrl) {
        return {
          sources: [{ format: 'mp4', url: videoUrl }],
          url: videoUrl,
          mimeType: 'video/mp4',
          previewImage: this.getPreviewImage(block, imgIdx),
        }
      }
      let videoObj = null
      if (typeof window !== 'undefined' && window.innerWidth <= 768) {
        videoObj = block[`mobile_video_${imgIdx}`] || block[`video_${imgIdx}`] || null
      } else {
        videoObj = block[`video_${imgIdx}`] || block[`mobile_video_${imgIdx}`] || null
      }
      if (
        videoObj &&
        videoObj.sources &&
        Array.isArray(videoObj.sources) &&
        videoObj.sources.some(src => src.format === 'mp4' && src.url)
      ) {
        return {
          ...videoObj,
          url: videoObj.sources.find(src => src.format === 'mp4' && src.url)?.url,
          mimeType: 'video/mp4',
        }
      }
      return null
    },

    /**
     * Get the preview image for a video or fallback to the block image.
     * @param {Object} block - Block object.
     * @param {Number} imgIdx - Image index.
     * @returns {Object}
     */
    getPreviewImage(block, imgIdx) {
      const video = this.getResponsiveVideo(block, imgIdx)
      if (video && video.previewImage) {
        return video.previewImage
      }
      return this.getResponsiveImage(block, imgIdx)
    },

    /**
     * Get the style object for a collage item based on
     * the number of blocks in the row.
     * @param {Object} block - Block object.
     * @returns {Object}
     */
    getItemStyle(block) {
      const indexes = this.getBlockImageIndexes(block)
      const count = indexes.length || 1
      const gap = Number(this.gap) || 0
      const totalGap = gap * (count - 1)
      return {
        width: `calc((100% - ${totalGap}px) / ${count})`,
        flex: `0 0 calc((100% - ${totalGap}px) / ${count})`,
        maxWidth: `calc((100% - ${totalGap}px) / ${count})`,
      }
    },

    /**
     * Get the video player icon color for a block and image index.
     * @param {Object} block - Block object.
     * @param {Number} imgIdx - Image index.
     * @returns {String}
     */
    getVideoIconColor(block, imgIdx) {
      return block[`block_video_player_icon_color_${imgIdx}`] || 'black'
    },

    /**
     * Get overlay class for a given type.
     * @param {String} type - 'title', 'subtitle', or 'description'
     * @param {Object} block
     * @param {Number} imgIdx
     * @param {String} fallback
     * @returns {String}
     */
    getOverlayClass(type, block, imgIdx, fallback) {
      return `collage-block__${type}-overlay--${block[`${type}_position_${imgIdx}`] || fallback}`
    },

    /**
     * Get overlay style for a given type.
     * @param {Object} block
     * @param {Number} imgIdx
     * @param {String} type
     * @returns {Object}
     */
    getOverlayStyle(block, imgIdx, type) {
      return { color: this.getTextColor(block, imgIdx, type) }
    },
  },
}
</script>

<style lang="scss">
@import './collage-block';
</style>
